import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import { SessionHistoryItem } from '../../services/sessionService';
import { useUpdateLastPlayed } from '../../hooks/useUpdateLastPlayed';
import { sessionCompletionService } from '../../services/sessionCompletionService';

interface SessionListProps {
  sessions: SessionHistoryItem[] | null;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  deletingSessions: Set<number>;
  deleteSession: (sessionId: number) => void;
  loadMore: () => void;
  sessionType: 'books' | 'visualization';
  userId: string | number;
  emptyMessage: string;
  refetchSessions?: () => void;
  optimisticBumpSession?: (sessionId: number) => void;
}

export const SessionList: React.FC<SessionListProps> = ({
  sessions,
  isLoading,
  error,
  hasMore,
  deletingSessions,
  deleteSession,
  loadMore,
  sessionType,
  userId,
  emptyMessage,
  refetchSessions,
  optimisticBumpSession
}) => {
  const navigate = useNavigate();
  const { updateBooks, updateVisualization } = useUpdateLastPlayed();
  const [openCheckMenu, setOpenCheckMenu] = useState<number | null>(null);
  const [openDeleteMenu, setOpenDeleteMenu] = useState<number | null>(null);
  const [completingSessions, setCompletingSessions] = useState<Set<number>>(new Set());
  const checkMenuRef = useRef<HTMLDivElement>(null);

  const isBookSession = (session: SessionHistoryItem) => {
    return session.userGoal || session.bookTitle || session.coreInsight;
  };

  const isSessionChecked = (session: SessionHistoryItem) => {
    return session.listened === true;
  };

  const handleCompleteSession = useCallback(async (session: SessionHistoryItem) => {
    const sessionId = Number(session.sessionId) || Number(session.id);
    
    if (completingSessions.has(sessionId)) {
      return; // Already processing
    }

    setCompletingSessions(prev => new Set(prev).add(sessionId));
    setOpenCheckMenu(null);

    try {
      const result = await sessionCompletionService.markSessionCompleted(sessionId);
      
      if (result.success) {
        // Optimistically update the session to show as completed
        if (refetchSessions) {
          refetchSessions();
        }
      } else {
        console.error('Failed to mark session as completed:', result.error);
        // You could show a toast notification here
      }
    } catch (error) {
      console.error('Error completing session:', error);
    } finally {
      setCompletingSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  }, [completingSessions, refetchSessions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (openCheckMenu !== null) {
        // Check all check menu containers, not just the single ref
        const checkMenus = document.querySelectorAll('[data-check-menu]');
        let isInsideCheckMenu = false;
        checkMenus.forEach(menu => {
          if (menu.contains(target)) {
            isInsideCheckMenu = true;
          }
        });
        if (!isInsideCheckMenu) {
          setOpenCheckMenu(null);
        }
      }
      if (openDeleteMenu !== null) {
        // Find all delete menu containers and check if click is outside all of them
        const deleteMenus = document.querySelectorAll('[data-delete-menu]');
        let isInsideDeleteMenu = false;
        deleteMenus.forEach(menu => {
          if (menu.contains(target)) {
            isInsideDeleteMenu = true;
          }
        });
        if (!isInsideDeleteMenu) {
          setOpenDeleteMenu(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openCheckMenu, openDeleteMenu]);

  const handlePlaySession = useCallback(async (session: SessionHistoryItem) => {
    try {
      if (sessionType === 'books') {
        if (optimisticBumpSession) {
          try {
            optimisticBumpSession(Number(session.sessionId));
          } catch (error) {
            console.error('SessionList: Error in optimisticBumpSession for books:', error);
          }
        }
        await updateBooks(Number(session.sessionId));
      } else if (sessionType === 'visualization') {
        // Para visualization, algunas rutas usan id primario en vez de Id_session
        const idToSend = Number(session.sessionId) || Number(session.id);
        if (optimisticBumpSession) {
          try {
            optimisticBumpSession(idToSend);
          } catch (error) {
            console.error('SessionList: Error in optimisticBumpSession for visualization:', error);
          }
        }
        await updateVisualization(idToSend);
      }
    } catch (error) {
      console.error('SessionList: Error in handlePlaySession:', error);
      // noop: si falla la actualización no bloqueamos la reproducción
    } finally {
      navigate(
        `/player?userId=${userId}&sessionId=${session.sessionId}&sessionType=${sessionType}`
      );
    }
  }, [navigate, refetchSessions, sessionType, updateBooks, updateVisualization, optimisticBumpSession, userId]);

  return (
    <div className="flex flex-col flex-grow min-h-0 mt-3">
      <div className="flex-grow overflow-y-auto pb-4 mr-3 px-1" style={{ scrollbarGutter: 'stable', scrollbarWidth: 'thin' }}>
        {isLoading && !sessions ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 text-[#5CD2F4] animate-spin" />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center py-4 px-3 sm:px-4 md:px-6">
            <p className="text-white text-sm text-center max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl mx-auto">
              You have no sessions yet, click the button below to create your first session.
            </p>
          </div>
        ) : sessions && sessions.length > 0 ? (
          <>
            {sessions.map((session) => {
              const isChecked = isSessionChecked(session);
              const isDeleting = deletingSessions.has(session.sessionId);
              const isCompleting = completingSessions.has(Number(session.sessionId) || Number(session.id));
              
              return (
                <div
                  key={session.id}
                  className={`bg-[#444444] rounded-lg p-3 sm:p-4 min-h-[70px] sm:min-h-[85px] flex-shrink-0 mb-2 cursor-pointer transition-all duration-200 hover:bg-[#555555] hover:scale-[1.005] relative ${openCheckMenu === session.id || openDeleteMenu === session.id ? 'z-50' : 'z-10'} ${
                    isDeleting ? 'opacity-50 pointer-events-none' : ''
                  }`}
                  onClick={() => !isDeleting && handlePlaySession(session)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 items-center">
                      <p className="text-white text-sm font-medium">
                        {isBookSession(session) ? 
                          (session.userGoal || 'Book Summary') : 
                          (session.goal || 'Session Summary')
                        }
                      </p>
                      <p className="text-xs text-gray-400">
                        {isBookSession(session) && session.bookTitle && session.bookAuthor ? 
                          `${session.bookTitle} by ${session.bookAuthor}` : 
                          session.createdAt ? new Date(session.createdAt).toLocaleDateString() : 'Unknown date'
                        }
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      {isChecked && (
                        <div className="relative" data-check-menu ref={checkMenuRef}>
                          <button
                            className="p-2 hover:bg-white/10 rounded-full transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenCheckMenu(openCheckMenu === session.id ? null : session.id);
                            }}
                          >
                            <Check 
                              size={20} 
                              className="text-[#5CD2F4]"
                            />
                          </button>

                          {openCheckMenu === session.id && (
                            <div className="absolute right-0 top-full mt-1 bg-[#333333] rounded-lg shadow-lg border border-gray-600 z-[80] min-w-[120px]">
                              <button
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-400 hover:bg-[#444444] transition-colors z-[80] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCompleteSession(session);
                                }}
                                disabled={isCompleting}
                              >
                                {isCompleting ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Completing...
                                  </>
                                ) : (
                                  <>
                                    <svg 
                                      width="14" 
                                      height="14" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                      className="text-blue-400"
                                    >
                                      <path d="M20 6L9 17l-5-5"></path>
                                    </svg>
                                    Complete
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {!isChecked && (
                        <div className="p-2">
                          <Check 
                            size={20} 
                            className="text-gray-400"
                          />
                        </div>
                      )}
                      <div className="relative" data-delete-menu>
                        <button
                          className="p-2 hover:bg-white/10 rounded-full transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDeleteMenu(openDeleteMenu === session.id ? null : session.id);
                          }}
                        >
                          <svg 
                            width="15" 
                            height="15" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                            className="text-white"
                          >
                            <circle cx="12" cy="12" r="1"></circle>
                            <circle cx="19" cy="12" r="1"></circle>
                            <circle cx="5" cy="12" r="1"></circle>
                          </svg>
                        </button>

                        {openDeleteMenu === session.id && (
                          <div className="absolute right-0 top-full mt-1 bg-[#333333] rounded-lg shadow-lg border border-gray-600 z-[80] min-w-[120px]">
                            <button
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-[#444444] transition-colors z-[80] rounded-t-lg disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDeleteMenu(null);
                                const confirmed = window.confirm('Are you sure you want to delete this session? This action cannot be undone.');
                                if (confirmed) {
                                  deleteSession(session.sessionId);
                                }
                              }}
                              disabled={isDeleting}
                            >
                              <svg 
                                width="14" 
                                height="14" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                className="text-red-400"
                              >
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              </svg>
                              {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-400 text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>

      {hasMore === true && sessions && sessions.length > 0 && (
        <div className="flex justify-center pt-4 pb-4">
          <button
            onClick={loadMore}
            disabled={isLoading}
            className="px-6 py-2 text-sm bg-[#444444] rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#6A6A6A] transition-colors"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}    

    </div>
  );
};
